import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { Check, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { IMAGES } from "@/lib/constants";
import { BookingFlow } from "@/components/BookingFlow";
import { useGuestyListings } from "@/components/LiveAvailability";
import { useExperiences } from "@/hooks/useExperiences";

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6 },
};

const amenities = [
  "Full Kitchen", "Private Deck", "AC & Heating", "High-Speed Wi-Fi",
  "BBQ Grill", "Fire Pit", "Free Parking", "Pet Friendly",
  "Self Check-In", "Queen & King Beds", "Hot Water", "Outdoor Seating",
];

const galleryImages = [
  IMAGES.cabin1, IMAGES.cabin2, IMAGES.cabin3,
  IMAGES.gallery1, IMAGES.gallery2, IMAGES.gallery3,
];

export default function BetCabinPage() {
  const EXPERIENCES = useExperiences();
  const { listings } = useGuestyListings("cabin");
  const cabin = listings[0] || null;
  const cabinName = cabin?.title || "The Bet Cabin";
  const cabinGuests = cabin?.accommodates || 6;
  return (
    <>
      <Helmet>
        <title>Bet Cabin | Luxury Cabin Rental Near Lake Texoma - Texoma Resorts</title>
        <meta
          name="description"
          content="Book the Bet Cabin at Texoma Resorts. Our signature luxury cabin near Lake Texoma features a full kitchen, private deck, fire pit, and stunning nature views."
        />
      </Helmet>

      <section className="relative h-[60vh] min-h-[450px] flex items-end">
        <div className="absolute inset-0">
          <img src={IMAGES.cabin1} alt="Bet Cabin at Texoma Resorts near Lake Texoma" className="w-full h-full object-cover" />
          <div className="hero-overlay absolute inset-0" />
        </div>
        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-12 pb-12">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <p className="caption-text text-white/60 mb-3">Our Signature Stay</p>
            <h1 data-testid="cabin-title" className="font-heading text-4xl sm:text-5xl lg:text-7xl font-light text-white tracking-tight">
              {cabinName}
            </h1>
            <p data-testid="cabin-subtitle" className="font-body text-base md:text-lg text-white/70 max-w-xl mt-4">
              A luxury lakeside cabin retreat for up to {cabinGuests} guests
            </p>
          </motion.div>
        </div>
      </section>

      <section data-testid="cabin-booking-widget" className="py-8 md:py-12 bg-white border-b">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div {...fadeUp}>
            <p className="caption-text text-accent mb-3">Book Direct & Save</p>
            <h2 className="font-heading text-2xl md:text-3xl font-normal text-foreground mb-2">
              Book Your Stay
            </h2>
            <p className="font-body text-sm text-muted-foreground mb-6">
              Select your dates on the calendar, enter your details, and pay securely — your
              reservation is confirmed instantly.
            </p>
            <BookingFlow listingFilter="cabin" />
          </motion.div>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <motion.div {...fadeUp}>
              <p className="caption-text text-accent mb-3">About The Cabin</p>
              <h2 className="font-heading text-3xl md:text-4xl font-normal text-foreground mb-6">
                Your Lakeside Retreat
              </h2>
              <p className="font-body text-base text-muted-foreground leading-relaxed mb-4">
                The {cabinName} is our signature luxury accommodation at Serenity Village.
                Nestled among the trees near Lake Texoma, this fully furnished cabin offers
                the perfect blend of rustic charm and modern comfort.
              </p>
              <p className="font-body text-base text-muted-foreground leading-relaxed mb-8">
                Whether you are planning a romantic getaway, a family weekend, or a quiet
                escape from the city, the Bet Cabin provides everything you need for an
                unforgettable stay.
              </p>

              <h3 className="font-heading text-xl font-semibold text-foreground mb-4">Amenities</h3>
              <div className="grid grid-cols-2 gap-3 mb-8">
                {amenities.map((a) => (
                  <div key={a} className="flex items-center gap-2">
                    <Check size={14} className="text-primary shrink-0" />
                    <span className="font-body text-sm text-foreground/80">{a}</span>
                  </div>
                ))}
              </div>

              <div className="flex items-center gap-2 mb-4">
                <Badge data-testid="cabin-guests-badge" className="bg-primary text-white font-body text-xs">Up to {cabinGuests} Guests</Badge>
                <Badge variant="outline" className="font-body text-xs">Pet Friendly</Badge>
                <Badge variant="outline" className="font-body text-xs">Self Check-In</Badge>
              </div>
            </motion.div>

            <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.2 }}>
              <div className="grid grid-cols-2 gap-3">
                {galleryImages.slice(0, 4).map((img, i) => (
                  <div key={img} className={`img-hover-zoom rounded-lg overflow-hidden ${i === 0 ? 'col-span-2 aspect-video' : 'aspect-square'}`}>
                    <img src={img} alt={`Bet Cabin photo ${i + 1}`} className="w-full h-full object-cover" />
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-muted/50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div {...fadeUp}>
            <p className="caption-text text-accent mb-3">Enhance Your Stay</p>
            <h2 className="font-heading text-3xl md:text-4xl font-normal text-foreground mb-8">
              Suggested Add-Ons
            </h2>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {EXPERIENCES.slice(0, 3).map((exp, i) => (
              <motion.div key={exp.id} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.1 }}>
                <Card className="border border-border/50 card-lift h-full">
                  <CardContent className="p-5">
                    <h3 className="font-heading text-lg font-semibold text-foreground mb-2">{exp.name}</h3>
                    <p className="font-body text-sm text-muted-foreground mb-3">{exp.description}</p>
                    <div className="flex items-center justify-between">
                      <span className="font-heading text-xl font-semibold text-primary">${exp.price}</span>
                      <Link to="/experiences">
                        <Button variant="outline" size="sm" className="font-body text-xs rounded-full">
                          Learn More
                        </Button>
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
